
import React from 'react';
import { AuthContext } from './AuthContext';
import { useAuthState } from './useAuthState';
import { useAuthActions } from './useAuthActions';
import { 
  hasRole as checkHasRole,
  hasAnyRole as checkHasAnyRole,
  canAccessUserManagement,
  canAccessProductDatabase,
  canAccessOrderProcessing,
  canAccessPaymentGateways,
  canAccessContentManagement as checkCanAccessContentManagement,
  canAccessReporting,
  AppRole
} from './auth-utils';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    user,
    session,
    loading,
    userRoles,
    isAdmin,
    isSuperAdmin,
    updateUserRoles
  } = useAuthState();

  const { signIn, signUp, signOut } = useAuthActions(updateUserRoles);

  // Role checking functions exposed in the context
  const hasRole = (role: AppRole): boolean => {
    return checkHasRole(userRoles, role);
  };

  const hasAnyRole = (roles: AppRole[]): boolean => {
    return checkHasAnyRole(userRoles, roles);
  };

  // Enhanced access control functions
  const canManageUsers = (): boolean => {
    return canAccessUserManagement(userRoles);
  };

  const canManageProducts = (level: 'full' | 'edit' | 'view' = 'view'): boolean => {
    return canAccessProductDatabase(userRoles, level);
  };

  const canManageOrders = (level: 'full' | 'basic' | 'refunds' = 'basic'): boolean => {
    return canAccessOrderProcessing(userRoles, level);
  };

  const canManagePayments = (level: 'configure' | 'manage' = 'manage'): boolean => {
    return canAccessPaymentGateways(userRoles, level);
  };

  const canManageContent = (): boolean => {
    return checkCanAccessContentManagement(userRoles);
  };

  const canAccessReports = (type: 'all' | 'stock' | 'orders' | 'blog' | 'financial'): boolean => {
    return canAccessReporting(userRoles, type);
  };

  // Function for content management access
  const canAccessContentManagement = (): boolean => {
    return checkCanAccessContentManagement(userRoles);
  };

  // Context value
  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    isAdmin,
    isSuperAdmin,
    userRoles,
    hasRole,
    hasAnyRole,
    // Enhanced access control functions
    canManageUsers,
    canManageProducts,
    canManageOrders,
    canManagePayments,
    canManageContent,
    canAccessReports,
    canAccessContentManagement
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
